import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { masterDataService } from "@/lib/services/master-data-service";
import type { MasterData } from "@/lib/types/master-data";
import type { RootState } from "@/lib/redux/store";

type MasterDataStatus = "idle" | "loading" | "succeeded" | "failed";

type MasterDataState = {
  data: MasterData | null;
  status: MasterDataStatus;
  error: string | null;
};

const initialState: MasterDataState = {
  data: null,
  status: "idle",
  error: null,
};

export const fetchMasterData = createAsyncThunk(
  "masterData/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await masterDataService.fetchMasterData();

      if (!response.success) {
        return rejectWithValue(response.message);
      }

      return response.data;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load master data";

      return rejectWithValue(message);
    }
  },
);

const masterDataSlice = createSlice({
  name: "masterData",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterData.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMasterData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(fetchMasterData.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to load master data";
      });
  },
});

export default masterDataSlice.reducer;

export const selectMasterData = (state: RootState) => state.masterData.data;
export const selectBranches = (state: RootState) =>
  state.masterData.data?.branches ?? [];
export const selectMasterDataStatus = (state: RootState) =>
  state.masterData.status;
export const selectMasterDataError = (state: RootState) =>
  state.masterData.error;
